import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import cx from 'classnames'

import Button from 'common/components/button'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { Check, FilterHub } from 'common/icons'

import type { ImageStatus } from '../store/imagesSlice'
import {
  initialState,
  onImageStatusFilterChange,
  onStatusFilterReset,
} from '../store/imagesSlice'

type Option = {
  key: Lowercase<ImageStatus | 'ALL'>
  label: string
  value?: ImageStatus
}

const options: Option[] = [
  { key: 'all', label: 'Show all', value: undefined },
  { key: 'active', label: 'Show active', value: 'ACTIVE' },
  { key: 'inactive', label: 'Show inactive', value: 'INACTIVE' },
  { key: 'archived', label: 'Show archived', value: 'ARCHIVED' },
]

const ImageStatusFilter = () => {
  const dispatch = useAppDispatch()
  const statusFilter = useAppSelector((state) => state.images.imageStatus)
  const [filter, setFilter] = useState<ImageStatus | undefined>(statusFilter)

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="icon"
          prefixIcon={<FilterHub isDefaultColor={false} size={6} />}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={4}
          className="z-40 px-4 py-2 bg-gold-50 border border-gold-200 rounded-sm min-w-[160px]"
        >
          {options.map(({ key, label, value }) => {
            const isActive = value === filter
            return (
              <div
                key={key}
                role="menuitem"
                className={cx(
                  'group flex items-center text-xs font-normal py-2 px-4 -mx-4 cursor-pointer hover:bg-gold-300 gap-2',
                  {
                    'text-base-700': isActive,
                    'text-gold-700': !isActive,
                  }
                )}
                onClick={() => setFilter(value)}
              >
                <Check
                  isDefaultColor={false}
                  size={4}
                  className={cx({
                    visible: isActive,
                    invisible: !isActive,
                  })}
                />
                {label}
              </div>
            )
          })}
          <hr className="w-full my-2 border-gold-400" />
          <div className="flex justify-between">
            <Button
              size="small"
              variant="link"
              onClick={() => {
                setFilter(initialState.imageStatus)
                dispatch(onStatusFilterReset())
              }}
            >
              Reset
            </Button>
            <Button
              onClick={() => dispatch(onImageStatusFilterChange(filter))}
              variant="primary"
              size="small"
            >
              Apply
            </Button>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export default ImageStatusFilter
